/** Mounts the Try it panel on any operation page that carries one. */

import { mountPanel } from './panel.ts';
import { readSpec } from './spec.ts';

/**
 * Panels mount lazily.
 *
 * Resolving the session means a cross-origin request to the auth host. Doing that on
 * every operation page view would put a request on the critical path for the large
 * majority of readers who never scroll down to the panel, so the mount waits until the
 * panel is actually near the viewport.
 */
export function initTryIt(): void {
  const roots = [...document.querySelectorAll<HTMLElement>('[data-tryit]')];
  if (!roots.length) return;

  const mount = (root: HTMLElement) => {
    if (root.dataset.tryitMounted) return;
    root.dataset.tryitMounted = 'true';
    const spec = readSpec(root);
    if (!spec) return;
    try {
      mountPanel(root, spec);
    } catch (error) {
      // A broken panel must not take the rest of the page's JavaScript with it; the
      // static summary stays on screen as the fallback.
      console.error('Try it panel failed to mount', error);
    }
  };

  if (!('IntersectionObserver' in window)) {
    roots.forEach(mount);
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        mount(entry.target as HTMLElement);
      }
    },
    { rootMargin: '400px 0px' },
  );

  for (const root of roots) observer.observe(root);
}
